import React, {useMemo, useState} from 'react'
import {Link} from "react-router-dom";
import {Alert, Divider, Input, Modal, Radio} from "antd";
import Form, {FormInputComponent} from '../../../components/form/Form';
import SpinLoader from "../../../components/SpinLoader";
import {useMutation} from "@tanstack/react-query";
import User from '../../../models/User';
import {useAPI} from "../../../config/API";
import LoadingBanner from "../../../components/LoadingBanner";

const ProfileCard = ({ talent }) => {


    return (
        <div className="w-full max-w-4xl bg-white rounded-lg shadow-lg border">
            <div className="p-4">
                <div className="flex items-center justify-between gap-6">
                    {/* Left Section: Image and Primary Info */}
                    <div className="flex items-center gap-4 min-w-0">
                        {/* Profile Image */}
                        <div className="shrink-0">
                            <img
                                src={talent.image_path || "/api/placeholder/96/96"}
                                alt={talent.fullName()}
                                className="w-20 h-20 grayscale rounded-full object-cover border-2 border-gray-100"
                            />
                        </div>

                        {/* Primary Info */}
                        <div className="min-w-[220px]">
                            <h2 className="text-xl font-semibold text-gray-900 truncate">{talent.fullName()}</h2>
                            <p className="text-sm text-gray-600 mb-2 truncate">{talent.job_title}</p>

                            {/* Contact Links */}
                            <div className="space-y-3">
                                {talent.email && (
                                    <a
                                        href={`mailto:${talent.email}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center text-xs text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap overflow-hidden"
                                    >
                                        <svg className="shrink-0 w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <span className="truncate">
                                            {talent.email}
                                        </span>
                                    </a>
                                )}
                                {talent.phone && (
                                    <a
                                        href={`tel:${talent.phone}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center text-xs text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                                    >
                                        <svg className="shrink-0 w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                        <span className="truncate">
                                            {talent.phone}
                                        </span>
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col flex-grow space-y-3 items-stretch">
                        <div className="flex flex-row gap-6 justify-between">
                            {/* Middle Section: Additional Info */}
                            <div className="space-y-3 min-w-[200px] shrink-0 self-end">
                                {talent.location?.full && (
                                    <div className="flex items-center text-xs text-gray-600 whitespace-nowrap">
                                        <svg className="shrink-0 w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span className="truncate">
                                            {talent.location.full}
                                        </span>
                                    </div>
                                )}
                                {talent.registered_at && (
                                    <div className="flex items-center text-xs text-gray-600 whitespace-nowrap">
                                        <svg className="shrink-0 w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                            <path d="M16 2v4M8 2v4M3 10h18" />
                                        </svg>
                                        <span className="truncate">
                                            Registered: {talent.registered_at ? TB.moment(talent.registered_at).format('ll') : 'N/A'}
                                        </span>
                                    </div>
                                )}

                            </div>

                            {/* View Profile Button */}
                            <div className="shrink-0 self-end">
                                <Link
                                    target="_blank"
                                    className="text-sm px-2 py-1.5 btn btn-xs btn-primary whitespace-nowrap"
                                    to={`/talent-profiles/${talent.id}`}
                                >
                                    View Profile
                                </Link>
                            </div>
                        </div>
                        {talent.portfolio_url && (
                            <div className="flex items-center text-xs text-gray-600">
                                <svg className="shrink-0 w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                                    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                                </svg>
                                <div className="flex flex-row items-center gap-2">
                                    <a
                                        href={talent.portfolio_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap">
                                        Portfolio
                                    </a>

                                    {talent.portfolio_url_notes && (
                                        <span title={talent.portfolio_url_notes}
                                              className="self-start text-xxs bg-gray-100 px-2 py-1 rounded">
                                            <i className="fas fa-unlock-alt"/>&nbsp;&nbsp;
                                            {talent.portfolio_url_notes}

                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};
const ApproveProfileModal = ({canApprove = true, ...props}) => {
    const [isApproveProfileModalOpen, setIsApproveProfileModalOpen] = useState(false);
    let {
        data: talent,
        isFetching: isLoading,
        error,
        refetch
    } = useAPI(['fetchForTalentAsManager', {talentId: props.talent?.id}], {
        enabled: isApproveProfileModalOpen,
        onSuccess: (data) => {
            // console.log(data, 'daaataaa')
            let profile = TB.pick({...data}, [
                'talent_profile_rejection_notes',
                'talent_profile_rejected_at',
                'talent_profile_approved_at',
                'talent_profile_admin_decision',
                'talent_profile_admin_notes',
                'latest_appeal',
            ]);

            profile.is_approved = !!profile.talent_profile_approved_at
            profile.is_rejected = !!profile.talent_profile_rejected_at

            // if (profile.latest_appeal) {
            //     profile.laste
            // }

            // console.log(profile, 'profile on success')
            // return TB.cloneDeep({...profile});
            return setFormModel(TB.cloneDeep({ ...profile }));
        },
        keepPreviousData: false
    })

    const [formModel, setFormModel] = useState({});

    const onChange = (model) => {
        let profile = {
            ...formModel
        };
        for (let [prop, value] of Object.entries(model)) {
            profile = TB.set(profile, prop, value)
        }
        console.log('onChange', profile)
        setFormModel(profile);
    }

    const updateProfileApprovalMutation = useMutation(['updateProfileApproval', props.talent?.id],
        async data => {
            const approval_props = TB.pick(data, ['talent_profile_admin_decision', 'talent_profile_admin_notes', 'latest_appeal'])

            if (data.latest_appeal) {
                if (data.latest_appeal.is_approved) {
                    // "is_approved"
                    // "admin_notes"
                    // "admin_decision"
                }

                if (data.latest_appeal.is_rejected) {
                    // "is_rejected"
                    // "rejection_notes"
                    // "admin_notes"
                    // "admin_decision"
                }
            } else {
                if (data.is_approved) {
                    approval_props.talent_profile_approved_at = new Date();
                }

                if (data.is_rejected) {
                    approval_props.talent_profile_rejected_at = new Date();
                    approval_props.talent_profile_rejection_notes = data.talent_profile_rejection_notes;
                }
            }

            const updatedUser = await User.updateTalentFromManager(talent.id, approval_props)

            if (!updatedUser) {
                throw new Error('Something went wrong!');
            }
            return updatedUser;
        }, {
        onSuccess: async (response) => {

            await TB.queryClient.setQueryData(['fetchForTalentAsManager', { talentId: props.talent?.id }], response)

            await setIsApproveProfileModalOpen(false)
            return TB.toast('User Profile Update')

        },
        onError: async (error) => {
            console.error("UpdateProfileApprovalMutation", error)
            return TB.showServerErrorToast()
        }
    })

    return (
        <div className="flex-1 flex items-stretch flex-col">
            <button
                type="button"
                className="btn btn-link btn-link-primary px-0 m-0 text-center"
                disabled={updateProfileApprovalMutation?.isLoading}
                onClick={() => setIsApproveProfileModalOpen(true)}
            >
                {canApprove ? 'Profile Approval' : 'Profile Info'}
            </button>

            <Modal
                title="Profile Approve Modal"
                closeable={!updateProfileApprovalMutation.isLoading}
                maskClosable={!updateProfileApprovalMutation.isLoading}
                style={{ top: 20 }}
                destroyOnClose={true}
                width={850}
                footer={null}
                open={isApproveProfileModalOpen}
                // onCancel={handleClose}
                onClose={() => setIsApproveProfileModalOpen(false)}
                onCancel={() => setIsApproveProfileModalOpen(false)}
            >
                {(!talent || isLoading) ? (
                    <LoadingBanner />
                ) : (
                    <SpinLoader isLoading={updateProfileApprovalMutation.isLoading}
                        className="flex flex-col items-stretch">

                        <ProfileCard talent={talent} />

                        {talent.canViewClientProfile() && (
                            <React.Fragment>
                                <Divider className="mb-0"/>

                                <Form
                                    // ref={formRef}
                                    title={null}
                                    subtitle={null}
                                    showRequired
                                    minimal
                                    submitButton={false}
                                    onSubmit={async () => {
                                        updateProfileApprovalMutation.mutateAsync(formModel)
                                    }}
                                    onChange={onChange}
                                    model={formModel}
                                    fields={[]}
                                    isDisabled={updateProfileApprovalMutation.isLoading}
                                >
                                    <div className="pt-6">

                                        {!!talent.latest_appeal && (
                                            <Alert
                                                message="Talent Submitted Appeal For Approval"
                                                type="warning"
                                                showIcon
                                                description={(
                                                    <div className="flex flex-col gap-3 items-stretch mt-2.5">
                                                        <div>
                                                            <p className="text-sm col-form-label font-medium">
                                                                Date Requested:
                                                            </p>
                                                            <p className="text-sm">
                                                                {TB.moment(talent.latest_appeal?.created_at).format('llll')}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <p className="text-sm col-form-label font-medium">
                                                                Message from talent:
                                                            </p>
                                                            <p className="text-sm">
                                                                {talent.latest_appeal?.notes || 'No reason given'}
                                                            </p>
                                                        </div>

                                                        <div className="flex flex-col gap-5 justify-between items-stretch flex-1">
                                                            <div className="flex flex-col gap-1.5 items-stretch">
                                                                <label className="col-form-label font-medium">
                                                                    Appeal Decision:
                                                                </label>
                                                                <Radio.Group
                                                                    value={formModel.latest_appeal?.is_approved ? 'approved' : (
                                                                        formModel.latest_appeal?.is_rejected ? 'rejected' : undefined
                                                                    )}
                                                                    onChange={e => {
                                                                        let assessment = { ...formModel };

                                                                        switch (e.target.value) {
                                                                            case 'approved':
                                                                                assessment = TB.set(assessment, `latest_appeal.is_approved`, true);
                                                                                assessment = TB.set(assessment, `latest_appeal.is_rejected`, false);
                                                                                break;
                                                                            case 'rejected':
                                                                                assessment = TB.set(assessment, `latest_appeal.is_approved`, false);
                                                                                assessment = TB.set(assessment, `latest_appeal.is_rejected`, true);
                                                                                break;
                                                                        }
                                                                        setFormModel(assessment)
                                                                    }}>
                                                                    <Radio value="approved">
                                                                        <span>
                                                                            Accept and Approve
                                                                        </span>
                                                                    </Radio>
                                                                    <Radio value="rejected">
                                                                        <span>
                                                                            Reject Approval
                                                                        </span>
                                                                    </Radio>
                                                                </Radio.Group>

                                                            </div>
                                                            {(formModel.latest_appeal?.is_rejected || formModel.latest_appeal?.rejection_notes) ? (
                                                                <FormInputComponent
                                                                    fieldName={`latest_appeal.rejection_notes`}
                                                                    value={formModel.latest_appeal?.rejection_notes}
                                                                    className="mt-4"
                                                                    labelClassName="flex flex-row items-center gap-3"
                                                                    label={(
                                                                        <span className="flex-grow flex flex-row items-center justify-between gap-5">
                                                                            <span className="font-medium">Appeal Rejection Feedback</span>
                                                                            <span className="text-danger">
                                                                                <b>Note:</b> This <b>WILL</b> be visible to talent
                                                                            </span>
                                                                        </span>
                                                                    )}
                                                                    placeholder={"Give feedback as to why this request was denied"}
                                                                    type={'antd-input'}
                                                                    required={formModel.latest_appeal?.is_rejected}
                                                                    maxLength={2000}
                                                                    rows={4}
                                                                >
                                                                    <Input.TextArea
                                                                        rows={4}
                                                                        disabled={!formModel.latest_appeal?.is_rejected}
                                                                    />

                                                                </FormInputComponent>
                                                            ) : <div className="mt-3" />}
                                                        </div>
                                                        <FormInputComponent
                                                            fieldName={`latest_appeal.admin_decision`}
                                                            value={talent.latest_appeal?.admin_decision}
                                                            className="mt-4"
                                                            label={(
                                                                <span
                                                                    className="flex flex-row items-center justify-between gap-5">
                                                                    <span className="font-medium">Reason For Appeal Decision <i>(required)</i>
                                                                    </span>
                                                                    <span className="text-danger">
                                                                        <b>Note:</b> This <b>WILL NOT</b> be visible to talent
                                                                    </span>
                                                                </span>
                                                            )}
                                                            placeholder={"Explain the reasoning for the decision"}
                                                            type={'antd-input'}
                                                            maxLength={2000}
                                                            rows={4}
                                                        >
                                                            <Input.TextArea
                                                                rows={4}
                                                                required
                                                            />

                                                        </FormInputComponent>
                                                    </div>
                                                )}
                                            />
                                        )}


                                        <div className="flex flex-col gap-3 items-stretch w[900px] max-w-full">

                                            <div className="flex flex-col gap-1.5 items-stretch">
                                                <label htmlFor="" className="col-form-label">
                                                    Profile Status
                                                </label>
                                                <Radio.Group
                                                    className="flex-1"
                                                    value={formModel.is_approved ? 'approved' : formModel.is_rejected ? 'rejected' : undefined}
                                                    // value={scoreItem?.is_approved ? 'approved' : (
                                                    //     scoreItem?.is_rejected ? 'rejected' : undefined
                                                    // )}
                                                    disabled={!!talent.latest_appeal}
                                                    onChange={e => {
                                                        let assessment = { ...formModel };

                                                        switch (e.target.value) {
                                                            case 'approved':
                                                                assessment.is_approved = true;
                                                                assessment.is_rejected = false;
                                                                // assessment = TB.set(assessment, `${propName}[${index}].is_approved`, true);
                                                                // if (propName === 'talent_disciplines') {
                                                                //     assessment = TB.set(assessment, `${propName}[${index}].is_rejected`, false);
                                                                // }
                                                                break;
                                                            case 'rejected':
                                                                assessment.is_approved = false;
                                                                assessment.is_rejected = true;


                                                                // assessment = TB.set(assessment, `${propName}[${index}].is_approved`, false);
                                                                // if (propName === 'talent_disciplines') {
                                                                //     assessment = TB.set(assessment, `${propName}[${index}].is_rejected`, true);
                                                                // }
                                                                break;
                                                        }
                                                        setFormModel(assessment)
                                                        // setChangeCount(changeCount + 1)
                                                    }}
                                                >
                                                    <Radio value="approved">
                                                        <span>
                                                            Approved
                                                        </span>
                                                    </Radio>
                                                    <Radio value="rejected">
                                                        <span>
                                                            Rejected
                                                        </span>
                                                    </Radio>
                                                </Radio.Group>
                                            </div>

                                            {(formModel?.is_rejected || formModel?.talent_profile_rejection_notes) && (
                                                <FormInputComponent
                                                    fieldName="talent_profile_rejection_notes"
                                                    value={formModel?.talent_profile_rejection_notes}
                                                    labelClassName="flex flex-row items-center gap-3"
                                                    label={(
                                                        // <span className="flex flex-row items-center justify-between gap-5">
                                                        //     <span className="font-medium">
                                                        //         Rejection Notes
                                                        //     </span>
                                                        //     <span className="text-danger">
                                                        //         <b>Note:</b> This <b>WILL</b> be visible to talent
                                                        //     </span>
                                                        // </span>

                                                        <span
                                                            className="flex-grow flex flex-row items-center justify-between gap-5">
                                                            <span className="font-medium">Rejection Notes <i>(required)</i>
                                                            </span>
                                                            <span className="text-danger">
                                                                <b>Note:</b> This <b>WILL</b> be visible to talent
                                                            </span>
                                                        </span>
                                                    )}
                                                    placeholder={"Give feedback to the talent for reason their profile was not approved"}
                                                    type={'antd-input'}
                                                    required
                                                    // className="flex-1"
                                                    maxLength={2000}
                                                >
                                                    <Input.TextArea
                                                        rows={4}
                                                        disabled={!formModel?.is_rejected || !!talent.latest_appeal}
                                                    />
                                                </FormInputComponent>
                                            )}

                                            <FormInputComponent
                                                fieldName="talent_profile_admin_decision"
                                                value={formModel?.talent_profile_admin_decision}
                                                labelClassName="flex flex-row items-center gap-3"
                                                label={(
                                                    <span
                                                        className="flex-grow flex flex-row items-center justify-between gap-5">
                                                        <span className="font-medium">Reason For Decision <i>(required)</i>
                                                        </span>
                                                        <span className="text-danger">
                                                            <b>Note:</b> This <b>WILL NOT</b> be visible to talent
                                                        </span>
                                                    </span>
                                                )}
                                                // disabled={propName === 'talent_disciplines' && !scoreItem?.is_approved && !scoreItem?.is_rejected}
                                                className="mt-4"
                                                placeholder={"Explain the reasoning for the decision"}
                                                type={'antd-input'}
                                                required={true}
                                                maxLength={2000}
                                            >
                                                <Input.TextArea
                                                    disabled={!!talent.latest_appeal}
                                                    rows={4}
                                                />
                                            </FormInputComponent>

                                            <FormInputComponent
                                                fieldName="talent_profile_admin_notes"
                                                value={formModel?.talent_profile_admin_notes}
                                                label={(
                                                    <span className="flex flex-row items-center justify-between gap-5">
                                                        <span className="font-medium">Admin Notes <i>(optional)</i></span>
                                                        <span className="text-danger">
                                                            <b>Note:</b> This <b>WILL NOT</b> be visible to talent
                                                        </span>
                                                    </span>
                                                )}
                                                className="mt-4"
                                                placeholder={"Write any additional administrative notes here"}
                                                type={'antd-input'}
                                                required={false}
                                                maxLength={2000}
                                            >
                                                <Input.TextArea
                                                    rows={4}
                                                />
                                            </FormInputComponent>
                                        </div>
                                    </div>

                                    <div className="assessment-form-actions mt-10">
                                        <button
                                            type="button"
                                            disabled={updateProfileApprovalMutation.isLoading}
                                            className="btn btn-dark btn-sm mx-2"
                                            onClick={async () => {
                                                setIsApproveProfileModalOpen(false);
                                            }}
                                        >
                                            Cancel
                                        </button>

                                        <button
                                            type="submit"
                                            disabled={updateProfileApprovalMutation.isLoading}
                                            className="btn btn-primary btn-sm mx-2"
                                        >
                                            Update Profile
                                        </button>
                                    </div>
                                </Form>
                            </React.Fragment>
                        )}
                    </SpinLoader>
                )}
            </Modal>
        </div>
    )
}

export default ApproveProfileModal;